import React, { useState } from 'react';

/**
 * UserAvatar — Reusable avatar component (R2 URL or initials fallback)
 *
 * Props:
 *   avatarUrl: string | null  — R2 CDN URL (or legacy base64 data:image/...)
 *   name: string | null       — User's full name (for initials fallback)
 *   size: number              — Diameter in px (default 32)
 *   onClick: function | null  — Click handler
 *   style: object             — Extra inline styles
 */
const UserAvatar = ({ avatarUrl, name, size = 32, onClick, style }) => {
  const [imgError, setImgError] = useState(false);

  const getInitials = (n) => {
    if (!n) return '?';
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return n[0].toUpperCase();
  };

  const showImage = avatarUrl && !imgError;

  const baseStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    flexShrink: 0,
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  };

  if (showImage) {
    return (
      <img
        src={avatarUrl}
        alt={name || 'Avatar'}
        style={{
          ...baseStyle,
          objectFit: 'cover',
        }}
        onClick={onClick}
        onError={() => setImgError(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  // Fallback: initials with hash-based color gradient
  const hashStr = (str) => {
    let h = 0;
    for (let i = 0; i < (str || '').length; i++) {
      h = ((h << 5) - h) + str.charCodeAt(i);
      h = h & h;
    }
    return Math.abs(h);
  };
  const hue = hashStr(name || '') % 360;
  const fontSize = Math.max(Math.round(size * 0.4), 10);
  return (
    <div
      style={{
        ...baseStyle,
        background: `linear-gradient(135deg, hsl(${hue}, 65%, 55%) 0%, hsl(${hue}, 55%, 38%) 100%)`,
        color: '#fff',
        fontWeight: 600,
        fontSize: `${fontSize}px`,
        fontFamily: 'var(--dp-font)',
        letterSpacing: '0.5px',
        userSelect: 'none',
      }}
      onClick={onClick}
      title={name || undefined}
    >
      {getInitials(name)}
    </div>
  );
};

export default UserAvatar;
